import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "./StatusBadge";
import { RiskForm } from "./RiskForm";

export function DpiaTab({ meta }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/governance/dpia").then((r) => setD(r.data[0])); }, []);
  if (!d) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-accent" />;

  const patch = async (body, msg) => {
    try { setD((await api.patch(`/governance/dpia/${d.id}`, body)).data); toast.success(msg); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = (i, applies) => patch({ screening: d.screening.map((s, j) => (j === i ? { ...s, applies } : s)) }, "Screening updated");
  const addRisk = async (risk) => {
    try { setD((await api.post(`/governance/dpia/${d.id}/risks`, risk)).data); toast.success("Risk added"); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };

  return (
    <div className="space-y-5" data-testid="dpia-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-900">{d.title}</h2>
          <p className="max-w-3xl text-sm text-slate-500">{d.scope}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={d.status} testId="dpia-status" />
          <Select value={d.status} onValueChange={(v) => patch({ status: v }, "DPIA status updated")}>
            <SelectTrigger className="w-56" data-testid="dpia-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.dpia_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-slate-900">Screening</h3>
          <span className={`text-sm font-bold ${d.dpia_required ? "text-red-700" : "text-emerald-700"}`} data-testid="dpia-required">
            {d.dpia_required ? "Full DPIA required" : "DPIA not indicated"}
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {d.screening.map((s, i) => (
            <div key={s.criterion} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 text-sm last:border-0">
              <div><div className="font-semibold text-slate-800">{s.criterion}</div><div className="text-xs text-slate-500">{s.note}</div></div>
              <Switch checked={s.applies} onCheckedChange={(v) => toggle(i, v)} data-testid={`dpia-screen-${i}`} />
            </div>
          ))}
        </div>
      </Card>
      <Card className="border-slate-200 p-5 shadow-sm">
        <h3 className="font-heading font-bold text-slate-900">Risk register</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="dpia-risks">
            <thead className="text-xs uppercase text-slate-500">
              <tr><th className="py-2 pr-3">Risk</th><th className="pr-3">Likelihood</th><th className="pr-3">Severity</th><th className="pr-3">Mitigation</th><th className="pr-3">Residual</th><th>Owner</th></tr>
            </thead>
            <tbody>
              {d.risks.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="py-2 pr-3 font-semibold text-slate-800">{r.risk}</td><td className="pr-3">{r.likelihood}</td><td className="pr-3">{r.severity}</td>
                  <td className="pr-3 text-slate-600">{r.mitigation}</td><td className="pr-3 font-bold">{r.residual}</td><td className="text-slate-600">{r.owner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <RiskForm onAdd={addRisk} />
      </Card>
      {d.approved_by && <p className="text-xs text-slate-500" data-testid="dpia-approved">Approved by {d.approved_by} on {new Date(d.approved_at).toLocaleString()}</p>}
    </div>
  );
}
